import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Safari-friendly auth: send cookie if present, also bearer-fallback to localStorage token ──
// Saves us when Safari ITP drops the cross-site cookie on the deployed domain.
axios.interceptors.request.use((config) => {
  config.withCredentials = config.withCredentials !== false; // default true
  const token = (typeof localStorage !== 'undefined') && localStorage.getItem('ace_admin_token');
  if (token && !config.headers?.Authorization) {
    config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };
  }
  return config;
});

// On 401, drop a stale localStorage token so the next /m visit prompts re-unlock
axios.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && typeof localStorage !== 'undefined') {
      localStorage.removeItem('ace_admin_token');
    }
    return Promise.reject(err);
  }
);

// Vehicles API
export const getVehicles = async (params = {}) => {
  try {
    const response = await axios.get(`${API}/vehicles`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching vehicles:', error);
    throw error;
  }
};

export const getVehicleById = async (id) => {
  try {
    const response = await axios.get(`${API}/vehicles/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching vehicle:', error);
    throw error;
  }
};

// Categories API
export const getCategories = async () => {
  try {
    const response = await axios.get(`${API}/categories`);
    return response.data;
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};

// Reviews API
export const getVehicleReviews = async (vehicleId) => {
  try {
    const response = await axios.get(`${API}/vehicles/${vehicleId}/reviews`);
    return response.data;
  } catch (error) {
    console.error('Error fetching reviews:', error);
    throw error;
  }
};

export const createReview = async (reviewData) => {
  try {
    const response = await axios.post(`${API}/reviews`, reviewData);
    return response.data;
  } catch (error) {
    console.error('Error creating review:', error);
    throw error;
  }
};

// Bookings API
export const createBooking = async (bookingData) => {
  try {
    const response = await axios.post(`${API}/bookings`, bookingData);
    return response.data;
  } catch (error) {
    console.error('Error creating booking:', error);
    throw error;
  }
};

export const getBookingById = async (id) => {
  try {
    const response = await axios.get(`${API}/bookings/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching booking:', error);
    throw error;
  }
};

export const checkAvailability = async (vehicleId, checkIn, checkOut) => {
  try {
    const response = await axios.get(`${API}/vehicles/${vehicleId}/availability`, {
      params: { checkIn, checkOut }
    });
    return response.data;
  } catch (error) {
    console.error('Error checking availability:', error);
    throw error;
  }
};

// Consultation API
export const createConsultation = async (consultationData) => {
  try {
    const response = await axios.post(`${API}/consultations`, consultationData);
    return response.data;
  } catch (error) {
    console.error('Error creating consultation:', error);
    throw error;
  }
};

// Partner Application API
export const createPartnerApplication = async (applicationData) => {
  try {
    const response = await axios.post(`${API}/partners/apply`, applicationData);
    return response.data;
  } catch (error) {
    console.error('Error creating partner application:', error);
    throw error;
  }
};

// Contact Form API
export const createContactRequest = async (contactData) => {
  try {
    const response = await axios.post(`${API}/contacts`, contactData);
    return response.data;
  } catch (error) {
    console.error('Error creating contact request:', error);
    throw error;
  }
};

// Auth API
export const exchangeSession = async (sessionId) => {
  try {
    const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error exchanging session:', error);
    throw error;
  }
};

export const getCurrentUser = async () => {
  try {
    const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error getting current user:', error);
    throw error;
  }
};

export const logout = async () => {
  try {
    const response = await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error logging out:', error);
    throw error;
  }
};

// Admin API
export const getAdminStats = async () => {
  try {
    const response = await axios.get(`${API}/admin/stats`, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error getting admin stats:', error);
    throw error;
  }
};

export const getAdminConsultations = async (params = {}) => {
  try {
    const response = await axios.get(`${API}/admin/consultations`, { params, withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error getting consultations:', error);
    throw error;
  }
};

export const updateConsultationStatus = async (leadId, status) => {
  try {
    const response = await axios.patch(`${API}/admin/consultations/${leadId}/status?status=${status}`, {}, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error updating consultation status:', error);
    throw error;
  }
};

export const getAdminPartners = async (params = {}) => {
  try {
    const response = await axios.get(`${API}/admin/partners`, { params, withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error getting partners:', error);
    throw error;
  }
};

export const updatePartnerStatus = async (appId, status) => {
  try {
    const response = await axios.patch(`${API}/admin/partners/${appId}/status?status=${status}`, {}, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error updating partner status:', error);
    throw error;
  }
};

export const getAdminContacts = async (params = {}) => {
  try {
    const response = await axios.get(`${API}/admin/contacts`, { params, withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error getting contacts:', error);
    throw error;
  }
};

export const updateContactStatus = async (ticketId, status) => {
  try {
    const response = await axios.patch(`${API}/admin/contacts/${ticketId}/status?status=${status}`, {}, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error updating contact status:', error);
    throw error;
  }
};

// Payment API
export const createCheckoutSession = async (checkoutData) => {
  try {
    const response = await axios.post(`${API}/payments/checkout`, checkoutData, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error creating checkout:', error);
    throw error;
  }
};

export const getPaymentStatus = async (sessionId) => {
  try {
    const response = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error getting payment status:', error);
    throw error;
  }
};

// Client Portal API
export const clientExchangeSession = async (sessionId) => {
  try {
    const response = await axios.post(`${API}/client/auth/session`, { session_id: sessionId }, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error exchanging client session:', error);
    throw error;
  }
};

export const getClientMe = async () => {
  try {
    const response = await axios.get(`${API}/client/auth/me`, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error getting client:', error);
    throw error;
  }
};

export const clientLogout = async () => {
  try {
    const response = await axios.post(`${API}/client/auth/logout`, {}, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error logging out:', error);
    throw error;
  }
};

export const getClientDashboard = async () => {
  try {
    const response = await axios.get(`${API}/client/portal/dashboard`, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error getting client dashboard:', error);
    throw error;
  }
};

export const getClientBookings = async (params = {}) => {
  try {
    const response = await axios.get(`${API}/client/portal/bookings`, { params, withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error getting client bookings:', error);
    throw error;
  }
};

export const getClientBookingDetail = async (bookingId) => {
  try {
    const response = await axios.get(`${API}/client/portal/bookings/${bookingId}`, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error getting booking detail:', error);
    throw error;
  }
};

export const getClientPayments = async () => {
  try {
    const response = await axios.get(`${API}/client/portal/payments`, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error getting client payments:', error);
    throw error;
  }
};

export const getClientProfile = async () => {
  try {
    const response = await axios.get(`${API}/client/portal/profile`, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error getting client profile:', error);
    throw error;
  }
};

export const updateClientProfile = async (profileData) => {
  try {
    const response = await axios.patch(`${API}/client/portal/profile`, profileData, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error updating client profile:', error);
    throw error;
  }
};

export const getClientConsultations = async () => {
  try {
    const response = await axios.get(`${API}/client/portal/consultations`, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error getting client consultations:', error);
    throw error;
  }
};

// Loyalty & Referral API
export const getLoyaltyBalance = async () => {
  try {
    const response = await axios.get(`${API}/loyalty/balance`, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error getting loyalty balance:', error);
    throw error;
  }
};

export const getLoyaltyHistory = async () => {
  try {
    const response = await axios.get(`${API}/loyalty/history`, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error getting loyalty history:', error);
    throw error;
  }
};

export const redeemLoyaltyPoints = async (rewardId) => {
  try {
    const response = await axios.post(`${API}/loyalty/redeem`, { reward_id: rewardId }, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error redeeming points:', error);
    throw error;
  }
};

export const getReferralInfo = async () => {
  try {
    const response = await axios.get(`${API}/loyalty/referral`, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error getting referral info:', error);
    throw error;
  }
};

export const applyReferralCode = async (code, email) => {
  try {
    const response = await axios.post(`${API}/loyalty/referral/apply`, { code, email });
    return response.data;
  } catch (error) {
    console.error('Error applying referral code:', error);
    throw error;
  }
};
